import React, { useState, useEffect, useRef } from 'react';
import { useBrands, useIdeas } from '../hooks/useApi';
import apiService from '../services/api';

const EMPTY_FILTERS = {};

const MEDIA_TYPE_INFO = {
  image: { label: 'Image', accept: '.jpg,.jpeg,.png,.webp', maxMB: 10, icon: 'fas fa-image' },
  carousel_pdf: { label: 'PDF Carousel', accept: '.pdf', maxMB: 50, icon: 'fas fa-file-pdf' },
  video: { label: 'Video', accept: '.mp4', maxMB: 100, icon: 'fas fa-video' },
};

const PostModal = ({ isOpen, onClose, onSuccess, post = null }) => {
  const [formData, setFormData] = useState({
    content: '',
    brand: '',
    status: 'DRAFT',
    scheduled_time: '',
    post_type: ''
  });
  const [ideaInput, setIdeaInput] = useState('');
  const [showAIGeneration, setShowAIGeneration] = useState(true);
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [generatingContent, setGeneratingContent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaType, setMediaType] = useState('none');
  const [mediaPreview, setMediaPreview] = useState(null);
  const mediaInputRef = useRef(null);
  const [error, setError] = useState('');
  const [showIdeasPicker, setShowIdeasPicker] = useState(false);

  const { data: brandsData } = useBrands();
  const { data: ideasData } = useIdeas(EMPTY_FILTERS);
  const brands = brandsData?.results || [];
  const isEditing = !!post;

  // Get connected accounts for selected brand
  const selectedBrandObj = brands.find(b => b.id === parseInt(formData.brand));
  const connectedAccounts = (selectedBrandObj?.social_accounts || []).filter(a => a.connected);

  const platformInfo = {
    'LINKEDIN': { icon: 'fab fa-linkedin', name: 'LinkedIn', color: 'text-blue-700' },
    'X': { icon: 'fab fa-twitter', name: 'X', color: 'text-gray-900' },
    'FACEBOOK': { icon: 'fab fa-facebook', name: 'Facebook', color: 'text-blue-600' },
    'INSTAGRAM': { icon: 'fab fa-instagram', name: 'Instagram', color: 'text-pink-600' },
  };

  const togglePlatform = (platform) => {
    setSelectedPlatforms(prev =>
      prev.includes(platform)
        ? prev.filter(p => p !== platform)
        : [...prev, platform]
    );
  };

  useEffect(() => {
    if (post && isOpen) {
      // If we have full post data (content + brand ID), use it directly
      if (post.content && post.brand) {
        setFormData({
          content: post.content || '',
          brand: post.brand || '',
          status: post.status || 'DRAFT',
          scheduled_time: post.scheduled_time ? new Date(post.scheduled_time).toISOString().slice(0, 16) : '',
          post_type: post.post_type || ''
        });
      } else {
        // Fetch full post data from detail endpoint (list view only has content_preview)
        apiService.getPost(post.id).then(fullPost => {
          setFormData({
            content: fullPost.content || '',
            brand: fullPost.brand || '',
            status: fullPost.status || 'DRAFT',
            scheduled_time: fullPost.scheduled_time ? new Date(fullPost.scheduled_time).toISOString().slice(0, 16) : '',
            post_type: fullPost.post_type || ''
          });
        }).catch(() => {
          // Fallback to whatever data we have
          setFormData({
            content: post.content || post.content_preview || '',
            brand: post.brand || '',
            status: post.status || 'DRAFT',
            scheduled_time: post.scheduled_time ? new Date(post.scheduled_time).toISOString().slice(0, 16) : '',
            post_type: post.post_type || ''
          });
        });
      }
    } else if (!post) {
      setFormData({
        content: '',
        brand: '',
        status: 'DRAFT',
        scheduled_time: '',
        post_type: ''
      });
      setShowAIGeneration(!isEditing);
    }
    setError('');
    // Pre-fill idea input if coming from Ideas page
    if (post?.ideaText) {
      setIdeaInput(post.ideaText);
    } else {
      setIdeaInput('');
    }
    setSelectedPlatforms([]);
    setShowIdeasPicker(false);
    setMediaFile(null);
    setMediaType('none');
    setMediaPreview(null);
  }, [post, isOpen]);

  // Set default brand when brands load (only if no brand is selected yet)
  useEffect(() => {
    if (brands.length > 0 && !formData.brand && !post) {
      setFormData(prev => ({ ...prev, brand: brands[0].id }));
    }
  }, [brands.length, formData.brand, post]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const generateAIContent = async () => {
    if (!ideaInput.trim()) {
      setError('Please enter your post idea first');
      return;
    }

    setGeneratingContent(true);
    setError('');

    try {
      // Get the selected brand info for context
      const selectedBrand = brands.find(b => b.id === parseInt(formData.brand));
      const brandContext = selectedBrand ? ` for ${selectedBrand.name}` : '';

      const generateData = {
        idea: ideaInput.trim(),
        brand_context: brandContext,
        brand_id: formData.brand,
        post_type: formData.post_type,
      };
      if (selectedPlatforms.length === 1) {
        generateData.platform = selectedPlatforms[0];
      }
      const response = await apiService.generatePostContent(generateData);

      setFormData(prev => ({
        ...prev,
        content: response.generated_content || response.content
      }));

      setShowAIGeneration(false);
    } catch (err) {
      console.error('AI Content Generation Error:', err);
      setError('AI service unavailable. Please write your content manually.');
      setShowAIGeneration(false);
    } finally {
      setGeneratingContent(false);
    }
  };

  const handleManualContent = () => {
    setShowAIGeneration(false);
  };

  const handleBackToAI = () => {
    setShowAIGeneration(true);
    setFormData(prev => ({ ...prev, content: '' }));
  };

  const buildSubmitData = () => {
    if (mediaFile && mediaType !== 'none') {
      const fd = new FormData();
      fd.append('content', formData.content);
      fd.append('brand', parseInt(formData.brand));
      fd.append('status', formData.status);
      fd.append('post_type', formData.post_type);
      if (formData.scheduled_time) fd.append('scheduled_time', formData.scheduled_time);
      fd.append('media_type', mediaType);
      fd.append('media_file', mediaFile);
      return fd;
    }
    return {
      ...formData,
      brand: parseInt(formData.brand),
      scheduled_time: formData.scheduled_time || null,
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const submitData = buildSubmitData();

      let result;
      if (isEditing) {
        result = await apiService.updatePost(post.id, submitData);
      } else {
        result = await apiService.createPost(submitData);
      }

      onSuccess(result);
      onClose();
    } catch (err) {
      setError(err.message || `Failed to ${isEditing ? 'update' : 'create'} post`);
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async () => {
    setPublishing(true);
    setError('');

    try {
      const submitData = buildSubmitData();
      if (!(submitData instanceof FormData)) {
        submitData.status = 'DRAFT';
      }

      // Save the post first (create or update)
      let result;
      if (isEditing) {
        result = await apiService.updatePost(post.id, submitData);
      } else {
        result = await apiService.createPost(submitData);
      }

      // Now publish it to selected platforms (or all if none selected)
      const publishResult = await apiService.publishPost(result.id, selectedPlatforms);

      onSuccess(result, 'published');
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to publish post');
    } finally {
      setPublishing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-900/40 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 w-full max-w-2xl rounded-xl bg-white border border-gray-200">
        <div className="mt-3">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-heading font-medium text-gray-900">
              {isEditing ? 'Edit Post' : 'Create New Post'}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <i className="fas fa-times text-xl"></i>
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Post Type Selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Post Type
              </label>
              <div className="grid grid-cols-5 gap-2">
                {[
                  { value: 'POSITIONING', label: 'Positioning', desc: 'Signal', icon: 'fas fa-bullseye' },
                  { value: 'AUTHORITY', label: 'Authority', desc: 'Depth', icon: 'fas fa-crown' },
                  { value: 'PROOF', label: 'Proof', desc: 'Credibility', icon: 'fas fa-chart-bar' },
                  { value: 'INSIGHT', label: 'Insight', desc: 'Thinking', icon: 'fas fa-lightbulb' },
                  { value: 'INFRASTRUCTURE', label: 'Infra', desc: 'Differentiator', icon: 'fas fa-cogs' },
                ].map(type => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      post_type: prev.post_type === type.value ? '' : type.value
                    }))}
                    className={`flex flex-col items-center p-2.5 rounded-xl border-2 transition-colors text-center ${
                      formData.post_type === type.value
                        ? 'border-accent-500 bg-accent-50 text-accent-800'
                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    <i className={`${type.icon} text-lg mb-1`}></i>
                    <span className="text-xs font-medium">{type.label}</span>
                    <span className="text-xs mt-0.5 opacity-60">{type.desc}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* AI Content Generation Section */}
            {!isEditing && showAIGeneration && (
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                <div className="flex items-center mb-3">
                  <i className="fas fa-magic text-purple-600 mr-2"></i>
                  <h4 className="text-lg font-heading font-medium text-gray-900">AI Content Generator</h4>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Share your post idea and let AI create engaging content for you
                </p>

                <div className="space-y-4">
                  <div>
                    <label htmlFor="ideaInput" className="block text-sm font-medium text-gray-700 mb-1">
                      Your Post Idea
                    </label>
                    <textarea
                      id="ideaInput"
                      value={ideaInput}
                      onChange={(e) => setIdeaInput(e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-1 focus:ring-accent-300 focus:border-accent-300"
                      placeholder="e.g., Launch of our new eco-friendly product line, tips for remote work productivity, behind-the-scenes of our team meeting..."
                    />
                  </div>

                  {/* Pick from Ideas */}
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowIdeasPicker(!showIdeasPicker)}
                      className="inline-flex items-center text-sm text-amber-700 hover:text-amber-900"
                    >
                      <i className="fas fa-lightbulb mr-1"></i>
                      Pick from Ideas
                      <i className={`fas fa-chevron-${showIdeasPicker ? 'up' : 'down'} ml-1 text-xs`}></i>
                    </button>
                    {showIdeasPicker && (() => {
                      const unusedIdeas = (ideasData?.results || []).filter(idea => {
                        if (idea.used) return false;
                        if (formData.brand && idea.brand !== parseInt(formData.brand)) return false;
                        return true;
                      });
                      return (
                        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                          {unusedIdeas.length === 0 ? (
                            <div className="p-3 text-sm text-gray-500 text-center">
                              No unused ideas{formData.brand ? ' for this brand' : ''}
                            </div>
                          ) : (
                            unusedIdeas.map(idea => (
                              <button
                                key={idea.id}
                                type="button"
                                onClick={() => { setIdeaInput(idea.text); setShowIdeasPicker(false); }}
                                className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-accent-50 border-b border-gray-100 last:border-b-0"
                              >
                                <span className="line-clamp-2">{idea.text}</span>
                                {idea.platform && (
                                  <span className="text-xs text-gray-400 ml-1">({idea.platform})</span>
                                )}
                              </button>
                            ))
                          )}
                        </div>
                      );
                    })()}
                  </div>

                  <div className="flex space-x-3">
                    <button
                      type="button"
                      onClick={generateAIContent}
                      disabled={generatingContent || !ideaInput.trim()}
                      className="flex-1 inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-xl text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {generatingContent ? (
                        <>
                          <i className="fas fa-spinner fa-spin mr-2"></i>
                          Generating...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-magic mr-2"></i>
                          Generate Content
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={handleManualContent}
                      className="inline-flex items-center px-4 py-2 border border-gray-200 text-sm font-medium rounded-xl text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-300"
                    >
                      <i className="fas fa-edit mr-2"></i>
                      Write Manually
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Content Section */}
            {(!showAIGeneration || isEditing) && (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label htmlFor="content" className="block text-sm font-medium text-gray-700">
                    Post Content *
                  </label>
                  {!isEditing && !showAIGeneration && (
                    <button
                      type="button"
                      onClick={handleBackToAI}
                      className="text-xs text-purple-600 hover:text-purple-800"
                    >
                      <i className="fas fa-magic mr-1"></i>
                      Use AI Generator
                    </button>
                  )}
                </div>
                <textarea
                  id="content"
                  name="content"
                  required
                  rows={6}
                  value={formData.content}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-1 focus:ring-accent-300 focus:border-accent-300"
                  placeholder="What's on your mind?"
                />
                {formData.content && (
                  <div className="mt-2 text-xs text-gray-500">
                    {formData.content.length} characters
                  </div>
                )}
              </div>
            )}

            {/* Media Upload */}
            {(!showAIGeneration || isEditing) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Media Attachment
                </label>
                <div className="flex items-center gap-3 mb-2">
                  {Object.entries(MEDIA_TYPE_INFO).map(([type, info]) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => {
                        if (mediaType === type) {
                          setMediaType('none');
                          setMediaFile(null);
                          setMediaPreview(null);
                          if (mediaInputRef.current) mediaInputRef.current.value = '';
                        } else {
                          setMediaType(type);
                          setMediaFile(null);
                          setMediaPreview(null);
                          if (mediaInputRef.current) mediaInputRef.current.value = '';
                        }
                      }}
                      className={`inline-flex items-center px-3 py-1.5 rounded-xl text-xs font-medium border transition-colors ${
                        mediaType === type
                          ? 'border-accent-500 bg-accent-50 text-accent-800'
                          : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <i className={`${info.icon} mr-1.5`}></i>
                      {info.label}
                    </button>
                  ))}
                </div>
                {mediaType !== 'none' && (
                  <div className="space-y-2">
                    <input
                      ref={mediaInputRef}
                      type="file"
                      accept={MEDIA_TYPE_INFO[mediaType].accept}
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        const maxBytes = MEDIA_TYPE_INFO[mediaType].maxMB * 1024 * 1024;
                        if (file.size > maxBytes) {
                          setError(`File too large. Max ${MEDIA_TYPE_INFO[mediaType].maxMB}MB for ${MEDIA_TYPE_INFO[mediaType].label}`);
                          return;
                        }
                        setMediaFile(file);
                        if (mediaType === 'image') {
                          setMediaPreview(URL.createObjectURL(file));
                        } else {
                          setMediaPreview(null);
                        }
                      }}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-medium file:bg-accent-50 file:text-accent-700 hover:file:bg-accent-100"
                    />
                    <p className="text-xs text-gray-400">
                      Max {MEDIA_TYPE_INFO[mediaType].maxMB}MB. Accepted: {MEDIA_TYPE_INFO[mediaType].accept}
                    </p>
                    {mediaPreview && mediaType === 'image' && (
                      <div className="relative inline-block">
                        <img src={mediaPreview} alt="Preview" className="h-24 rounded-lg border object-cover" />
                        <button
                          type="button"
                          onClick={() => { setMediaFile(null); setMediaPreview(null); if (mediaInputRef.current) mediaInputRef.current.value = ''; }}
                          className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600"
                        >
                          <i className="fas fa-times" style={{fontSize: '8px'}}></i>
                        </button>
                      </div>
                    )}
                    {mediaFile && mediaType === 'carousel_pdf' && (
                      <div className="flex items-center gap-2 text-sm text-gray-700 bg-gray-50 px-3 py-2 rounded-xl">
                        <i className="fas fa-file-pdf text-red-500"></i>
                        <span className="truncate">{mediaFile.name}</span>
                        <span className="text-xs text-gray-400">({(mediaFile.size / (1024*1024)).toFixed(1)}MB)</span>
                        <button type="button" onClick={() => { setMediaFile(null); if (mediaInputRef.current) mediaInputRef.current.value = ''; }} className="ml-auto text-gray-400 hover:text-red-500">
                          <i className="fas fa-times"></i>
                        </button>
                      </div>
                    )}
                    {mediaFile && mediaType === 'video' && (
                      <div className="flex items-center gap-2 text-sm text-gray-700 bg-gray-50 px-3 py-2 rounded-xl">
                        <i className="fas fa-video text-blue-500"></i>
                        <span className="truncate">{mediaFile.name}</span>
                        <span className="text-xs text-gray-400">({(mediaFile.size / (1024*1024)).toFixed(1)}MB)</span>
                        <button type="button" onClick={() => { setMediaFile(null); if (mediaInputRef.current) mediaInputRef.current.value = ''; }} className="ml-auto text-gray-400 hover:text-red-500">
                          <i className="fas fa-times"></i>
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Brand Selection */}
            <div>
              <label htmlFor="brand" className="block text-sm font-medium text-gray-700 mb-1">
                Brand *
              </label>
              <select
                id="brand"
                name="brand"
                required
                value={formData.brand}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-1 focus:ring-accent-300 focus:border-accent-300"
              >
                <option value="">Select a brand</option>
                {brands.map(brand => (
                  <option key={brand.id} value={brand.id}>
                    {brand.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Status */}
            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-1 focus:ring-accent-300 focus:border-accent-300"
              >
                <option value="DRAFT">Draft</option>
                <option value="SCHEDULED">Scheduled</option>
              </select>
            </div>

            {/* Scheduled Time */}
            {formData.status === 'SCHEDULED' && (
              <div>
                <label htmlFor="scheduled_time" className="block text-sm font-medium text-gray-700 mb-1">
                  Scheduled Time
                </label>
                <input
                  type="datetime-local"
                  id="scheduled_time"
                  name="scheduled_time"
                  value={formData.scheduled_time}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-1 focus:ring-accent-300 focus:border-accent-300"
                />
              </div>
            )}

            {/* Platform Selection for Publishing */}
            {connectedAccounts.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Publish to
                </label>
                <div className="flex flex-wrap gap-2">
                  {connectedAccounts.map(account => {
                    const info = platformInfo[account.platform] || { icon: 'fas fa-globe', name: account.platform, color: 'text-gray-600' };
                    const isSelected = selectedPlatforms.includes(account.platform);
                    return (
                      <button
                        key={account.id}
                        type="button"
                        onClick={() => togglePlatform(account.platform)}
                        className={`inline-flex items-center px-3 py-2 rounded-xl text-sm font-medium border transition-colors ${
                          isSelected
                            ? 'border-accent-500 bg-accent-50 text-accent-800'
                            : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        <i className={`${info.icon} mr-2 ${isSelected ? info.color : ''}`}></i>
                        {info.name}
                        {isSelected && <i className="fas fa-check ml-2 text-accent-900"></i>}
                      </button>
                    );
                  })}
                </div>
                {selectedPlatforms.length === 0 && (
                  <p className="text-xs text-gray-400 mt-1">Select platforms to enable publishing</p>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center px-4 py-2 border border-gray-200 text-sm font-medium rounded-xl text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-300"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || publishing}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-xl text-accent-900 bg-accent-600 hover:bg-accent-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-300 disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2"></i>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save mr-2"></i>
                    {isEditing ? 'Save Draft' : 'Save as Draft'}
                  </>
                )}
              </button>
              {selectedPlatforms.length > 0 && (
                <button
                  type="button"
                  onClick={handlePublish}
                  disabled={loading || publishing || !formData.content.trim()}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-xl text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                >
                  {publishing ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Publishing...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-paper-plane mr-2"></i>
                      Publish Now
                    </>
                  )}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PostModal;
